<template>
  <div class="tender-search-wrap">
    <div
      ref="searchRoot"
      class="tender-search tender-search--trailing-actions"
      :class="{
        'tender-search--focused': isSearchFocused,
      }"
      @click="focusInput"
    >
      <div class="tender-search__lead-icon" aria-hidden="true">
        <v-icon class="tender-search__icon" small>mdi-magnify</v-icon>
      </div>
      <div ref="body" class="tender-search__body">
        <v-chip
          v-if="showEllipsis"
          x-small
          label
          class="tender-search__chip tender-search__chip--ellipsis"
          :aria-label="`${hiddenChipCount} more keywords`"
        >
          {{ ellipsisLabel }}
        </v-chip>
        <v-chip
          v-for="(chip, index) in displayedChips"
          :key="chipKey(chip, index)"
          color="rgb(255, 20, 99)"
          x-small
          label
          close
          class="tender-search__chip"
          @click:close="$emit('remove-keyword', chip)"
        >
          {{ chip.key }}
        </v-chip>
        <input
          ref="input"
          v-model="searchInput"
          type="text"
          class="tender-search__input"
          :placeholder="visibleChips.length ? '' : 'Separate keywords with Tab'"
          autocomplete="off"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
          @keydown="onKeywordKeydown"
          @paste="onKeywordsPaste"
        />
      </div>
      <v-tooltip bottom open-delay="400">
        <template v-slot:activator="{ on, attrs }">
          <div class="tender-search__history" v-bind="attrs" v-on="on">
            <button
              type="button"
              class="tender-search__history-btn search-history-trigger"
              aria-label="Recent searches"
              @click.stop="onHistoryClick"
            >
              <v-icon small color="rgba(18, 37, 58, 0.65)">mdi-history</v-icon>
            </button>
          </div>
        </template>
        <span>Recent searches</span>
      </v-tooltip>
      <v-tooltip bottom open-delay="400">
        <template v-slot:activator="{ on, attrs }">
          <div
            class="tender-search__filter"
            :class="{ 'tender-search__filter--active': hasAdvancedFilters }"
            v-bind="attrs"
            v-on="on"
          >
            <button
              type="button"
              class="tender-search__filter-btn"
              :aria-label="filterAriaLabel"
              @click.stop="onFilterClick"
            >
              <v-icon
                small
                :color="hasAdvancedFilters ? 'rgb(255, 20, 99)' : 'rgba(18, 37, 58, 0.65)'"
              >
                mdi-filter
              </v-icon>
            </button>
            <span
              v-if="hasAdvancedFilters"
              class="tender-search__filter-badge"
              aria-hidden="true"
            >
              {{ filterBadgeLabel }}
            </span>
          </div>
        </template>
        <span>{{ filterTooltip }}</span>
      </v-tooltip>
      <v-tooltip v-if="showResetButton" bottom open-delay="400">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            class="tender-search__reset-btn"
            aria-label="Clear all keywords and filters"
            v-bind="attrs"
            v-on="on"
            @click.stop="onResetClick"
          >
            <v-icon small color="rgba(18, 37, 58, 0.65)">mdi-close</v-icon>
          </v-btn>
        </template>
        <span>Clear all keywords and filters</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { splitKeywordTokens } from '@/utils/tenderSearchKeywords';

const MAX_CHIP_BODY_HEIGHT = 52;
const MIN_TRAILING_CHIPS = 2;

export default {
  name: 'TenderSearch',

  props: {
    headerState: {
      type: Object,
      default: () => ({
        keywordChips: [],
        hasAdvancedFilters: false,
        advancedFilterCount: 0,
      }),
    },
  },

  data() {
    return {
      searchInput: '',
      isSearchFocused: false,
      // 0 = show all chips; when > 0, number of trailing chips to display
      collapsedChipCount: 0,
      measureFrame: null,
      resizeObserver: null,
    };
  },

  watch: {
    visibleChips() {
      this.scheduleMeasure();
    },
  },

  mounted() {
    if (typeof ResizeObserver !== 'undefined' && this.$refs.searchRoot) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.$refs.searchRoot);
    }
    this.scheduleMeasure();
  },

  beforeDestroy() {
    if (this.measureFrame) {
      cancelAnimationFrame(this.measureFrame);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  computed: {
    keywordChips() {
      return this.headerState.keywordChips || [];
    },

    visibleChips() {
      return this.keywordChips.filter((chip) => chip && String(chip.key || '').trim());
    },

    displayedChips() {
      const chips = this.visibleChips;
      if (!this.collapsedChipCount || chips.length === 0) {
        return chips;
      }
      return chips.slice(-this.collapsedChipCount);
    },

    showEllipsis() {
      return this.collapsedChipCount > 0 && this.hiddenChipCount > 0;
    },

    hiddenChipCount() {
      return this.visibleChips.length - this.displayedChips.length;
    },

    ellipsisLabel() {
      return this.hiddenChipCount > 0 ? `… +${this.hiddenChipCount}` : '…';
    },

    advancedFilterCount() {
      return this.headerState.advancedFilterCount || 0;
    },

    hasAdvancedFilters() {
      return this.advancedFilterCount > 0;
    },

    filterBadgeLabel() {
      const n = this.advancedFilterCount;
      return n > 9 ? '9+' : String(n);
    },

    filterTooltip() {
      const n = this.advancedFilterCount;
      if (n === 0) return 'Open advanced search';
      return n === 1 ? '1 advanced filter active' : `${n} advanced filters active`;
    },

    filterAriaLabel() {
      const n = this.advancedFilterCount;
      if (n === 0) return 'Open advanced search';
      return `Open advanced search, ${n} filters active`;
    },

    showResetButton() {
      return this.visibleChips.length > 0 || this.hasAdvancedFilters;
    },
  },

  methods: {
    chipKey(chip, index) {
      const key = chip && chip.key;
      const fullIndex = this.visibleChips.indexOf(chip);
      const resolvedIndex = fullIndex >= 0 ? fullIndex : index;
      return key ? `${key}-${resolvedIndex}` : `chip-${resolvedIndex}`;
    },

    focusInput() {
      const input = this.$refs.input;
      if (input) input.focus();
    },

    onSearchFocus() {
      this.isSearchFocused = true;
      this.$emit('search-focus');
      this.$nextTick(() => {
        this.emitKeywordsOverflowIfActive();
      });
    },

    onSearchBlur() {
      this.isSearchFocused = false;
    },

    onTabAddChip() {
      const trimmed = (this.searchInput || '').trim();
      if (!trimmed) return;
      this.$emit('add-keyword', trimmed);
      this.searchInput = '';
    },

    onKeywordKeydown(e) {
      if (e.key === 'Tab') {
        e.preventDefault();
        this.onTabAddChip();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.onSubmit();
      } else if (e.key === ',') {
        e.preventDefault();
        this.onTabAddChip();
      } else if (e.key === 'Backspace' && !this.searchInput && this.visibleChips.length) {
        this.$emit('remove-keyword', this.visibleChips[this.visibleChips.length - 1]);
      }
    },

    onKeywordsPaste(e) {
      const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
      const keywords = splitKeywordTokens(text);
      if (keywords.length === 0) return;
      e.preventDefault();
      keywords.forEach((kw) => this.$emit('add-keyword', kw));
      this.searchInput = '';
    },

    onSubmit() {
      const trimmed = (this.searchInput || '').trim();
      this.$emit('submit', trimmed);
      if (trimmed) {
        this.searchInput = '';
      }
    },

    onHistoryClick() {
      this.$emit('open-search-history');
    },

    onFilterClick() {
      this.$emit('open-advanced-search');
    },

    onResetClick() {
      this.searchInput = '';
      this.$emit('reset');
    },

    scheduleMeasure() {
      if (this.measureFrame) {
        cancelAnimationFrame(this.measureFrame);
      }
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measureChipLayout();
      });
    },

    bodyOverflows() {
      const body = this.$refs.body;
      if (!body) return false;
      return body.scrollHeight > MAX_CHIP_BODY_HEIGHT;
    },

    async measureChipLayout() {
      const chips = this.visibleChips;
      if (chips.length <= MIN_TRAILING_CHIPS) {
        this.collapsedChipCount = 0;
        return;
      }

      this.collapsedChipCount = 0;
      await this.$nextTick();

      if (!this.bodyOverflows()) {
        return;
      }

      for (let trailingCount = chips.length - 1; trailingCount >= MIN_TRAILING_CHIPS; trailingCount -= 1) {
        this.collapsedChipCount = trailingCount;
        await this.$nextTick();
        if (!this.bodyOverflows()) {
          this.emitKeywordsOverflowIfActive();
          return;
        }
      }

      this.collapsedChipCount = MIN_TRAILING_CHIPS;
      this.emitKeywordsOverflowIfActive();
    },

    emitKeywordsOverflowIfActive() {
      if (this.showEllipsis && this.isSearchFocused) {
        this.$emit('keywords-overflow');
      }
    },
  },
};
</script>

<style scoped>
.tender-search-wrap {
  width: 100%;
}

.tender-search {
  --tender-search-row: 28px;

  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 2px 8px 2px 2px;
  background-color: #f5f6f8;
  border: 1px solid rgba(18, 37, 58, 0.12);
  border-radius: 6px;
  cursor: text;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.tender-search--focused {
  border-color: rgba(255, 20, 99, 0.45);
  box-shadow: 0 0 0 2px rgba(255, 20, 99, 0.14);
}

.tender-search--trailing-actions {
  padding-right: 12px;
}

.tender-search__lead-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--tender-search-row);
  height: var(--tender-search-row);
  margin-left: 2px;
}

.tender-search__icon {
  color: rgba(18, 37, 58, 0.65) !important;
}

.tender-search__body {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: var(--tender-search-row);
  max-height: 52px;
  min-width: 0;
  overflow: hidden;
}

.tender-search__chip {
  flex-shrink: 0;
}

.tender-search__chip--ellipsis {
  background-color: rgba(18, 37, 58, 0.12) !important;
  border-color: rgba(18, 37, 58, 0.12) !important;
  color: rgba(18, 37, 58, 0.65) !important;
  cursor: default;
  pointer-events: none;
}

.tender-search__input {
  flex: 1 1 80px;
  min-width: 80px;
  height: var(--tender-search-row);
  border: 0;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  line-height: var(--tender-search-row);
  padding: 0;
  margin: 0;
  color: #12253a;
  caret-color: #12253a;
}

.tender-search__input::placeholder {
  color: rgba(18, 37, 58, 0.55);
  opacity: 1;
}

.tender-search__history {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: var(--tender-search-row);
  margin-left: 2px;
  cursor: default;
}

.tender-search__history-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--tender-search-row);
  height: var(--tender-search-row);
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.tender-search__history-btn:hover {
  background: rgba(18, 37, 58, 0.06);
}

.tender-search__filter {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: var(--tender-search-row);
  margin-left: 2px;
  margin-right: 2px;
  cursor: default;
}

.tender-search__filter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--tender-search-row);
  height: var(--tender-search-row);
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.tender-search__filter-btn:hover {
  background: rgba(18, 37, 58, 0.06);
}

.tender-search__filter--active {
  padding-right: 2px;
}

.tender-search__filter--active .tender-search__filter-btn:hover {
  background: rgba(255, 20, 99, 0.1);
}

.tender-search__filter-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 12px;
  height: 12px;
  padding: 0 2px;
  border-radius: 6px;
  background-color: rgb(255, 20, 99);
  color: #fff;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
}

.tender-search__reset-btn {
  flex-shrink: 0;
  align-self: center;
  width: var(--tender-search-row) !important;
  height: var(--tender-search-row) !important;
  margin: 0 2px 0 0 !important;
}

.tender-search__reset-btn >>> .v-btn__content {
  opacity: 1;
}

.tender-search__lead-icon >>> .v-icon,
.tender-search__history-btn >>> .v-icon,
.tender-search__filter-btn >>> .v-icon {
  margin: 0 !important;
  line-height: 1;
}
</style>
